import {
  Box,
  Button,
  Divider,
  Flex,
  Icon,
  ModalBody,
  ModalCloseButton,
  ModalHeader,
  Text,
  useBreakpointValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import { ReactNode } from 'react'
import { Capacitor } from '@capacitor/core'
import { useAuth, useDisableUser, useResetMFA, useSendPwResetEmail } from '../../../hooks/useAuth'
import handleErrorToast from '../../../components/common/toast/handleErrorToast'
import handleSuccessToast from '../../../components/common/toast/handleSuccessToast'

import { KeyPasswordFilled, SecondEmail, TrashFilled } from '../../../utils/constants/icons'
import ConfirmModal from '../../../components/common/modals/ConfirmModal'

interface MobileSettingRowProps {
  icon: ReactNode
  iconBg: string
  iconColor: string
  title: string
  description: ReactNode
  action?: ReactNode
  destructive?: boolean
}

const MobileSettingCard: React.FC<MobileSettingRowProps> = ({
  icon,
  iconBg,
  iconColor,
  title,
  description,
  action,
  destructive,
}) => {
  return (
    <Flex
      direction="column"
      bg="white"
      borderWidth="1px"
      borderColor={destructive ? '#FBE5DD' : 'neutral.100'}
      borderRadius="14px"
      p="16px"
      gap="12px"
    >
      <Flex align="center" gap="12px">
        <Flex
          w="36px"
          h="36px"
          minW="36px"
          borderRadius="10px"
          bg={iconBg}
          align="center"
          justify="center"
          color={iconColor}
          flexShrink={0}
        >
          {icon}
        </Flex>
        <Box flex="1" minW="0">
          <Text textStyle="b-md" fontWeight="510" color={destructive ? '#E4502F' : 'neutral.900'}>
            {title}
          </Text>
          <Text textStyle="b-sm" color="neutral.500" fontWeight="400" mt="2px" wordBreak="break-word">
            {description}
          </Text>
        </Box>
      </Flex>
      {action && <Box w="100%">{action}</Box>}
    </Flex>
  )
}

const AccountSettingsPage = () => {
  const { user, logout, logoutNative } = useAuth()
  const toast = useToast()
  const isNative = Capacitor.isNativePlatform()
  const doLogout = () => (isNative ? logoutNative() : logout())
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false

  const currEmail = user?.email ?? ''

  const resetMfaMutation = useResetMFA()
  const sendPwResetEmailMutation = useSendPwResetEmail()
  const disableUserMutation = useDisableUser()

  const { isOpen: isResetPasswordOpen, onOpen: onResetPasswordOpen, onClose: onResetPasswordClose } = useDisclosure()
  const {
    isOpen: isResetAuthenticatorOpen,
    onOpen: onResetAuthenticatorOpen,
    onClose: onResetAuthenticatorClose,
  } = useDisclosure()
  const { isOpen: isDeleteAccountOpen, onOpen: onDeleteAccountOpen, onClose: onDeleteAccountClose } = useDisclosure()

  const confirmModals = (
    <>
      <ConfirmModal
        isOpen={isResetPasswordOpen}
        onClose={onResetPasswordClose}
        onConfirm={() => {
          sendPwResetEmailMutation.mutate(user?.email ?? '', {
            onSuccess: () => {
              doLogout()
              handleSuccessToast(toast, 'Password reset sent!', 'An email has been sent to your email address to confirm the password change.')
            },
            onError: () => handleErrorToast(toast, 'Password Update Failed!', 'Your password could not be updated. Please try again.'),
          })
        }}
        icon={
          <Icon color="white" width="28px" height="28px" viewBox="0 0 28 28">
            <KeyPasswordFilled />
          </Icon>
        }
        isLoading={sendPwResetEmailMutation.isPending}
        title="Are you sure you want to reset your password?"
        description="You will receive an email with a link to reset your password. After confirming this you will be logged out."
      />
      <ConfirmModal
        isOpen={isResetAuthenticatorOpen}
        onClose={onResetAuthenticatorClose}
        onConfirm={() => {
          resetMfaMutation.mutate(undefined, {
            onSuccess: () => {
              handleSuccessToast(toast, 'MFA reset sent!', 'Please login and enroll a new authenticator to continue using the app')
              doLogout()
            },
            onError: () => handleErrorToast(toast, 'Password Update Failed!', 'Your password could not be updated. Please try again.'),
          })
        }}
        icon={
          <Icon color="white" width="28px" height="28px" viewBox="0 0 28 28">
            <KeyPasswordFilled />
          </Icon>
        }
        isLoading={resetMfaMutation.isPending}
        title="Are you sure you want to reset your MFA?"
        description="You will be logged out and need to re-enroll your authenticator to continue using the app."
      />
      <ConfirmModal
        isOpen={isDeleteAccountOpen}
        onClose={onDeleteAccountClose}
        onConfirm={() => disableUserMutation.mutate(undefined, {
          onSuccess: () => {
            doLogout()
            handleSuccessToast(toast, 'Account disabled!', 'Your account has been disabled.')
          },
          onError: () => handleErrorToast(toast, 'Account Disable Failed!', 'Your account could not be disabled. Please try again.'),
        })}
        isLoading={disableUserMutation.isPending}
        title="Are you sure you want to disable your account?"
        description="This action is irreversible. Your account will be locked and unusable until a administrator re-enables it."
        icon={
          <Icon color="white" width="32px" height="32px" viewBox="0 0 16 16">
            <TrashFilled />
          </Icon>
        }
      />
    </>
  )

  if (isMobile) {
    return (
      <>
        {confirmModals}
        <Flex direction="column" gap="20px" pb="8px">
          <Box>
            <Text fontSize="20px" fontWeight="600" color="neutral.900" lineHeight="1.3">
              Account Settings
            </Text>
            <Text mt="4px" fontWeight="400" textStyle="b-sm" color="neutral.500">
              Keep your personal details up-to-date for a more personalized experience.
            </Text>
          </Box>

          <Flex direction="column" gap="10px">
            <MobileSettingCard
              iconBg="primary.50"
              iconColor="primary.500"
              icon={
                <Icon w="18px" h="18px" viewBox="0 0 16 16">
                  <SecondEmail />
                </Icon>
              }
              title="Email Address"
              description={
                <Text as="span" wordBreak="break-all" textStyle="b-sm" color="neutral.500">
                  Your email is{' '}
                  <Text as="span" fontWeight="510" color="neutral.900">
                    {currEmail}
                  </Text>
                </Text>
              }
            />

            <MobileSettingCard
              iconBg="primary.50"
              iconColor="primary.500"
              icon={
                <Icon w="18px" h="18px" viewBox="0 0 28 28">
                  <KeyPasswordFilled />
                </Icon>
              }
              title="Password"
              description={
                <>
                  Securely managed by{' '}
                  <Text as="span" fontWeight="510" color="neutral.900">
                    Auth0
                  </Text>
                </>
              }
              action={
                <Button
                  w="100%"
                  h="44px"
                  variant="outline"
                  borderColor="neutral.100"
                  color="neutral.900"
                  fontSize="14px"
                  fontWeight="510"
                  borderRadius="10px"
                  onClick={() => onResetPasswordOpen()}
                  isLoading={sendPwResetEmailMutation.isPending}
                  _hover={{ bg: 'neutral.50' }}
                  _active={{ transform: 'scale(0.99)' }}
                >
                  Forgot Password
                </Button>
              }
            />

            <MobileSettingCard
              iconBg="primary.50"
              iconColor="primary.500"
              icon={
                <Icon w="18px" h="18px" viewBox="0 0 28 28">
                  <KeyPasswordFilled />
                </Icon>
              }
              title="Multi-Factor Authentication"
              description={
                <>
                  Securely managed by{' '}
                  <Text as="span" fontWeight="510" color="neutral.900">
                    Auth0
                  </Text>
                </>
              }
              action={
                <Button
                  w="100%"
                  h="44px"
                  variant="outline"
                  borderColor="neutral.100"
                  color="neutral.900"
                  fontSize="14px"
                  fontWeight="510"
                  borderRadius="10px"
                  onClick={() => onResetAuthenticatorOpen()}
                  isLoading={resetMfaMutation.isPending}
                  _hover={{ bg: 'neutral.50' }}
                  _active={{ transform: 'scale(0.99)' }}
                >
                  Reset Authenticator
                </Button>
              }
            />

            <MobileSettingCard
              destructive
              iconBg="#FFF1EC"
              iconColor="#E4502F"
              icon={
                <Icon w="18px" h="18px" viewBox="0 0 16 16">
                  <TrashFilled />
                </Icon>
              }
              title="Disable Account"
              description="This action will lock your account until an administrator re-enables it."
              action={
                <Button
                  w="100%"
                  h="44px"
                  bg="#E4502F"
                  color="white"
                  fontWeight="510"
                  fontSize="14px"
                  borderRadius="10px"
                  _hover={{ bg: '#CC4528' }}
                  _active={{ transform: 'scale(0.99)' }}
                  onClick={() => onDeleteAccountOpen()}
                  leftIcon={
                    <Icon viewBox="0 0 18 18" w="16px" h="16px" color="white" fill="none">
                      <TrashFilled />
                    </Icon>
                  }
                >
                  Disable Account
                </Button>
              }
            />
          </Flex>
        </Flex>
      </>
    )
  }

  return (
    <>
      {confirmModals}
      <Box m="40px 20px 20px 20px" overflowY="auto">
        <ModalHeader>
          <Box display="flex">
            <Text fontWeight="510" textStyle="h5" mb="8px">
              Account Settings
            </Text>
          </Box>
          <Box display="flex" justifyContent="center">
            <Text fontWeight="400" textStyle="b-sm" color="neutral.500">
              Stay connected seamlessly by keeping your personal details up-to-date. Easily edit your information for a
              more personalized experience with us.
            </Text>
          </Box>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody position="relative">
          <Box display="flex" justifyContent="space-between">
            <Box>
              <Text textStyle="b-lg" fontWeight="400" color="neutral.900">
                Email Address
              </Text>
              <Text textStyle="b-sm" fontWeight="400" color="neutral.500">
                Your email address is{' '}
                <Text display="inline" fontWeight="510" color="neutral.900">
                  {currEmail}
                </Text>
              </Text>
            </Box>
          </Box>

          <Divider m="24px 0px" />

          <Box display="flex" justifyContent="space-between">
            <Box>
              <Text textStyle="b-lg" fontWeight="400" color="neutral.900">
                Password
              </Text>
              <Text textStyle="b-sm" fontWeight="400" color="neutral.500">
                Your password is securely managed by{' '}
                <Text display="inline" fontWeight="510" color="neutral.900">
                  Auth0
                </Text>
              </Text>
            </Box>
            <Button
              color="neutral.900"
              fontSize="14px"
              height="40px"
              mr="2px"
              onClick={() => onResetPasswordOpen()}
              isLoading={sendPwResetEmailMutation.isPending}
            >
              Forgot Password
            </Button>
          </Box>

          <Divider margin="24px 0px" />

          <Box display="flex" justifyContent="space-between">
            <Box>
              <Text textStyle="b-lg" fontWeight="400" color="neutral.900">
                MFA
              </Text>
              <Text textStyle="b-sm" fontWeight="400" color="neutral.500">
                Your MFA is securely managed by{' '}
                <Text display="inline" fontWeight="510" color="neutral.900">
                  Auth0
                </Text>
              </Text>
            </Box>
            <Button
              color="neutral.900"
              fontSize="14px"
              height="40px"
              mr="2px"
              onClick={() => onResetAuthenticatorOpen()}
              isLoading={sendPwResetEmailMutation.isPending}
            >
              Reset Authenticator
            </Button>
          </Box>

          <Divider margin="24px 0px" />

          <Box display="flex" justifyContent="space-between">
            <Box>
              <Text textStyle="b-lg" fontWeight="400" color="neutral.900">
                Disable Account
              </Text>
              <Text textStyle="b-sm" fontWeight="400" color="neutral.500">
                This action will lock your account until a administrator re-enables it.
              </Text>
            </Box>
            <Button
              mr="2px"
              variant="warning"
              h="40px"
              onClick={() => onDeleteAccountOpen()}
              leftIcon={
                <Icon viewBox="0 0 18 18" w="18px" h="18px" color="white" fill="none">
                  <TrashFilled />
                </Icon>
              }
            >
              Disable Account
            </Button>
          </Box>
        </ModalBody>
      </Box>
    </>
  )
}

export default AccountSettingsPage
